import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import {
  fetchPublicEvents,
  fetchPublicMatches,
  fetchPublicTrainings,
  registerMemberForEvent,
  type PublicEventItem,
  type PublicMatchItem,
  type PublicTrainingItem
} from "@/features/public/api";
import { getEventDisplayTitle, getPublicEventDisplayType, type PublicEventDisplayType } from "@/features/events/presentation";
import { readStoredMemberUser, restoreMemberAccessToken, subscribeToMemberSessionChanges } from "@/features/public/memberSession";
import { formatEventScheduleLine } from "@/shared/events/dateRange";
import { useRealtimeInvalidation } from "@/shared/realtime/useRealtimeInvalidation";

type SportTab = "calendrier" | "resultats" | "evenements";
type AgendaType = PublicEventDisplayType;

type AgendaItem = {
  id: string;
  source: "event" | "match" | "training";
  sourceId: string;
  titre: string;
  type: AgendaType;
  date: string;
  heure: string;
  dateFin: string | null;
  heureFin: string | null;
  lieu: string;
  participants: number;
  maxParticipants: number;
  description: string;
  sortAt: number;
  isLive: boolean;
  isPast: boolean;
};

type EventActionState = "register" | "login" | "closed" | "full" | null;

type ResultCard = {
  id: string;
  competition: string;
  equipe1: string;
  equipe2: string;
  score1: number;
  score2: number;
  date: string;
  type: string;
};

const typeConfig: Record<AgendaType, { color: string; bg: string; label: string; icon: string }> = {
  match: { color: "#1A1A1A", bg: "#11111110", label: "Match", icon: "ri-football-line" },
  entrainement: { color: "#1A1A1A", bg: "#11111110", label: "Entraînement", icon: "ri-run-line" },
  tournoi: { color: "#1A1A1A", bg: "#11111110", label: "Tournoi", icon: "ri-trophy-line" },
  general: { color: "#1A1A1A", bg: "#11111110", label: "Général", icon: "ri-calendar-event-line" }
};

const tabs: Array<{ id: SportTab; label: string; icon: string; path: string }> = [
  { id: "calendrier", label: "Calendrier", icon: "ri-calendar-line", path: "/sport/calendrier" },
  { id: "resultats", label: "Résultats", icon: "ri-bar-chart-line", path: "/sport/resultats" },
  { id: "evenements", label: "Événements", icon: "ri-star-line", path: "/sport/evenements" }
];

const filters = ["Tous", "Match", "Entraînement", "Tournoi", "Général"] as const;

function normalizeText(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function formatDate(dateStr: string) {
  const date = new Date(`${dateStr}T12:00:00`);

  return {
    day: date.getDate().toString().padStart(2, "0"),
    month: date.toLocaleDateString("fr-FR", { month: "short" }).toUpperCase(),
    weekday: date.toLocaleDateString("fr-FR", { weekday: "long" }),
    full: date.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })
  };
}

function toTimestamp(date: string, heure: string) {
  return new Date(`${date}T${heure}:00`).getTime();
}

function normalizeTrainingTime(value: string) {
  const match = value.match(/\b([01]\d|2[0-3]):([0-5]\d)\b/);
  return match?.[0] ?? "08:00";
}

function nextDateForTrainingDay(day: string) {
  const dayIndexMap = new Map([
    ["dimanche", 0],
    ["lundi", 1],
    ["mardi", 2],
    ["mercredi", 3],
    ["jeudi", 4],
    ["vendredi", 5],
    ["samedi", 6]
  ]);
  const targetDay = dayIndexMap.get(normalizeText(day)) ?? new Date().getDay();
  const today = new Date();
  const date = new Date(today);
  const diff = (targetDay - today.getDay() + 7) % 7;
  date.setDate(today.getDate() + diff);

  return date.toISOString().split("T")[0] ?? today.toISOString().split("T")[0] ?? "";
}

function resolveSportIcon(sport: string) {
  const normalizedSport = normalizeText(sport);

  if (normalizedSport.includes("football")) return "ri-football-line";
  if (normalizedSport.includes("basket")) return "ri-basketball-line";
  if (normalizedSport.includes("tennis")) return "ri-ping-pong-line";
  if (normalizedSport.includes("volley")) return "ri-volleyball-line";
  if (normalizedSport.includes("nat")) return "ri-swimming-line";

  return "ri-trophy-line";
}

function mapPublicEventToAgenda(event: PublicEventItem): AgendaItem | null {
  const mappedType = getPublicEventDisplayType(event.type);

  if (!mappedType || event.statut !== "PUBLISHED") {
    return null;
  }

  const sortAt = toTimestamp(event.date, event.heure);

  return {
    id: `event:${event.id}`,
    source: "event",
    sourceId: event.id,
    titre: getEventDisplayTitle(event.titre, mappedType),
    type: mappedType,
    date: event.date,
    heure: event.heure,
    dateFin: event.dateFin,
    heureFin: event.heureFin,
    lieu: event.lieu,
    participants: event.participants,
    maxParticipants: event.maxParticipants,
    description: event.description,
    sortAt,
    isLive: false,
    isPast: sortAt < Date.now()
  };
}

function mapPublicMatchToAgenda(match: PublicMatchItem): AgendaItem | null {
  if (match.statut === "CANCELLED" || match.statut === "COMPLETED") {
    return null;
  }

  const sortAt = toTimestamp(match.date, match.heure);

  return {
    id: `match:${match.id}`,
    source: "match",
    sourceId: match.id,
    titre: `${match.sport} - ${match.equipeDomicile} vs ${match.equipeExterieur}`,
    type: match.type === "TOURNAMENT" ? "tournoi" : "match",
    date: match.date,
    heure: match.heure,
    dateFin: null,
    heureFin: null,
    lieu: match.lieu,
    participants: 2,
    maxParticipants: 2,
    description: `Rencontre programmée entre ${match.equipeDomicile} et ${match.equipeExterieur}.`,
    sortAt,
    isLive: match.statut === "LIVE",
    isPast: match.statut !== "LIVE" && sortAt < Date.now()
  };
}

function mapPublicTrainingToAgenda(training: PublicTrainingItem): AgendaItem {
  const date = nextDateForTrainingDay(training.jour);
  const heure = normalizeTrainingTime(training.heure);
  const sortAt = toTimestamp(date, heure);

  return {
    id: `training:${training.id}`,
    source: "training",
    sourceId: training.id,
    titre: `Entraînement ${training.equipeNom}`,
    type: "entrainement",
    date,
    heure,
    dateFin: null,
    heureFin: null,
    lieu: training.lieu,
    participants: 0,
    maxParticipants: 0,
    description: `${training.sport} · ${training.equipeNom} · ${training.jour} à ${training.heure}`,
    sortAt,
    isLive: false,
    isPast: false
  };
}

function mapCompletedMatchToResult(match: PublicMatchItem): ResultCard | null {
  if (match.statut !== "COMPLETED" || match.scoreDomicile === null || match.scoreExterieur === null) {
    return null;
  }

  const competition =
    match.type === "LEAGUE"
      ? `Championnat ${match.sport}`
      : match.type === "CUP"
        ? `Coupe ${match.sport}`
        : match.type === "TOURNAMENT"
          ? `Tournoi ${match.sport}`
          : `Rencontre ${match.sport}`;

  return {
    id: `match-result:${match.id}`,
    competition,
    equipe1: match.equipeDomicile,
    equipe2: match.equipeExterieur,
    score1: match.scoreDomicile,
    score2: match.scoreExterieur,
    date: match.date,
    type: match.sport
  };
}

function computeStandings(results: ResultCard[]) {
  const footballResults = results.filter((result) => normalizeText(result.type).includes("football"));
  const table = new Map<
    string,
    { equipe: string; j: number; g: number; n: number; p: number; pts: number; diff: number; butsPour: number }
  >();

  footballResults.forEach((result) => {
    const left = table.get(result.equipe1) ?? { equipe: result.equipe1, j: 0, g: 0, n: 0, p: 0, pts: 0, diff: 0, butsPour: 0 };
    const right = table.get(result.equipe2) ?? { equipe: result.equipe2, j: 0, g: 0, n: 0, p: 0, pts: 0, diff: 0, butsPour: 0 };

    left.j += 1;
    right.j += 1;
    left.diff += result.score1 - result.score2;
    right.diff += result.score2 - result.score1;
    left.butsPour += result.score1;
    right.butsPour += result.score2;

    if (result.score1 > result.score2) {
      left.g += 1;
      left.pts += 3;
      right.p += 1;
    } else if (result.score1 < result.score2) {
      right.g += 1;
      right.pts += 3;
      left.p += 1;
    } else {
      left.n += 1;
      right.n += 1;
      left.pts += 1;
      right.pts += 1;
    }

    table.set(left.equipe, left);
    table.set(right.equipe, right);
  });

  return Array.from(table.values())
    .sort((left, right) => {
      if (right.pts !== left.pts) return right.pts - left.pts;
      if (right.diff !== left.diff) return right.diff - left.diff;
      if (right.butsPour !== left.butsPour) return right.butsPour - left.butsPour;
      return left.equipe.localeCompare(right.equipe, "fr");
    })
    .map((row, index) => ({
      pos: index + 1,
      ...row
    }));
}

function sortAgendaItems(items: AgendaItem[]) {
  return [...items].sort((left, right) => {
    if (left.isLive !== right.isLive) {
      return left.isLive ? -1 : 1;
    }

    if (left.isPast !== right.isPast) {
      return left.isPast ? 1 : -1;
    }

    return left.sortAt - right.sortAt;
  });
}

function getEventActionState(event: AgendaItem, isLoggedIn: boolean): EventActionState {
  if (event.source !== "event") {
    return null;
  }

  if (event.participants >= event.maxParticipants) {
    return "full";
  }

  if (event.isPast) {
    return "closed";
  }

  return isLoggedIn ? "register" : "login";
}

export default function SportPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [filterType, setFilterType] = useState<(typeof filters)[number]>("Tous");
  const [agendaItems, setAgendaItems] = useState<AgendaItem[]>([]);
  const [resultCards, setResultCards] = useState<ResultCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [memberUser, setMemberUser] = useState(() => readStoredMemberUser());
  const [memberAccessToken, setMemberAccessToken] = useState<string | null>(null);
  const [registeringEventId, setRegisteringEventId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const activeTab: SportTab = useMemo(() => {
    if (location.pathname.endsWith("/resultats")) return "resultats";
    if (location.pathname.endsWith("/evenements")) return "evenements";
    return "calendrier";
  }, [location.pathname]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const [matchesResult, eventsResult, trainingsResult] = await Promise.allSettled([
      fetchPublicMatches(),
      fetchPublicEvents({ status: "PUBLISHED", page: 1, limit: 100 }),
      fetchPublicTrainings()
    ]);

    const matches = matchesResult.status === "fulfilled" ? matchesResult.value : [];
    const events = eventsResult.status === "fulfilled" ? eventsResult.value.items : [];
    const trainings = trainingsResult.status === "fulfilled" ? trainingsResult.value : [];

    const nextAgendaItems = [
      ...matches.map(mapPublicMatchToAgenda).filter((item): item is AgendaItem => Boolean(item)),
      ...events.map(mapPublicEventToAgenda).filter((item): item is AgendaItem => Boolean(item)),
      ...trainings.map(mapPublicTrainingToAgenda)
    ];

    const nextResultCards = matches
      .map(mapCompletedMatchToResult)
      .filter((item): item is ResultCard => Boolean(item))
      .sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime());

    setAgendaItems(sortAgendaItems(nextAgendaItems));
    setResultCards(nextResultCards);

    if (matchesResult.status === "rejected" && eventsResult.status === "rejected" && trainingsResult.status === "rejected") {
      setError("Impossible de charger les données sportives pour le moment.");
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const syncMemberSession = async () => {
      const nextUser = readStoredMemberUser();
      const nextToken = await restoreMemberAccessToken().catch(() => null);

      if (!isMounted) {
        return;
      }

      setMemberUser(nextUser);
      setMemberAccessToken(nextToken);
    };

    void syncMemberSession();
    const unsubscribe = subscribeToMemberSessionChanges(() => {
      void syncMemberSession();
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    void loadData();

    const handleWindowFocus = () => {
      void loadData();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void loadData();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [loadData]);

  useRealtimeInvalidation({
    topics: ["matches", "events", "trainings"],
    onInvalidate: () => {
      void loadData();
    }
  });

  useEffect(() => {
    if (!toast) {
      return;
    }

    const timeout = window.setTimeout(() => setToast(null), 3500);
    return () => window.clearTimeout(timeout);
  }, [toast]);

  const handleRegisterForEvent = useCallback(
    async (event: AgendaItem) => {
      if (event.source !== "event") {
        return;
      }

      if (!memberAccessToken || !memberUser) {
        navigate("/formulaires?tab=login&redirect=%2Fsport%2Fevenements");
        return;
      }

      setRegisteringEventId(event.sourceId);

      try {
        await registerMemberForEvent(memberAccessToken, event.sourceId);
        await loadData();
        setToast({
          type: "success",
          message: `Votre inscription à ${event.titre} a bien été enregistrée.`
        });
      } catch (caughtError) {
        const message = caughtError instanceof Error ? caughtError.message : "Inscription impossible pour le moment.";
        setToast({
          type: "error",
          message
        });
      } finally {
        setRegisteringEventId(null);
      }
    },
    [loadData, memberAccessToken, memberUser, navigate]
  );

  const filteredAgendaItems = useMemo(() => {
    if (filterType === "Tous") {
      return agendaItems;
    }

    const filtersMap: Record<Exclude<(typeof filters)[number], "Tous">, AgendaType> = {
      Match: "match",
      "Entraînement": "entrainement",
      Tournoi: "tournoi",
      Général: "general"
    };

    return agendaItems.filter((item) => item.type === filtersMap[filterType as Exclude<(typeof filters)[number], "Tous">]);
  }, [agendaItems, filterType]);

  const standings = useMemo(() => computeStandings(resultCards), [resultCards]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {toast ? (
        <div className={`fixed top-24 right-6 z-50 rounded-2xl px-5 py-3 text-sm font-medium text-white shadow-xl ${toast.type === "success" ? "bg-emerald-500" : "bg-red-500"}`}>
          {toast.message}
        </div>
      ) : null}

      <section className="relative">
        <div className="relative h-72 md:h-80">
          <img
            src="https://readdy.ai/api/search-image?query=african%20athletes%20sports%20training%20football%20basketball%20senegal%20dakar%20dynamic%20action%20colorful%20stadium&width=1600&height=400&seq=sport1&orientation=landscape"
            alt="Sport ASC Sonatel"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="max-w-[1400px] mx-auto px-6 w-full">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#E8521A]/20 text-[#FF8C5A] text-sm font-medium mb-4">
                Activités Sportives
              </span>
              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-3">Sport</h1>
              <p className="text-lg text-white/80 max-w-xl">
                Calendrier, résultats, événements et tout ce qui fait vibrer l&apos;ASC Sonatel
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="sticky top-20 z-40 bg-white border-b border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex gap-1 overflow-x-auto py-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => navigate(tab.path)}
                className={`flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? "bg-[#E8521A] text-white"
                    : "bg-orange-50 text-[#E8521A] border border-[#E8521A]/20 hover:bg-orange-100"
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-12">
        {error ? (
          <div className="mb-8 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-600">
            {error}
          </div>
        ) : null}

        {activeTab === "calendrier" && (
          <div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
              <div>
                <h2 className="text-3xl font-bold text-[#1A1A1A]">Calendrier Sportif</h2>
                <p className="text-gray-500 mt-1">Toutes les rencontres et les événements sportifs à venir</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {filters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setFilterType(filter)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      filterType === filter
                        ? "bg-[#E8521A] text-white"
                        : "bg-[#FAF9F7] text-gray-500 hover:text-[#E8521A]"
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-[320px] rounded-3xl border-2 border-gray-200 bg-white animate-pulse"></div>
                ))}
              </div>
            ) : filteredAgendaItems.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredAgendaItems.map((event) => {
                  const cfg = typeConfig[event.type];
                  const date = formatDate(event.date);
                  const pct = event.maxParticipants > 0 ? Math.round((event.participants / event.maxParticipants) * 100) : 0;
                  const actionState = getEventActionState(event, Boolean(memberUser));
                  const isRegistering = registeringEventId === event.sourceId;
                  const showParticipantMeter = event.source !== "training" && event.maxParticipants > 0;

                  return (
                    <div
                      key={event.id}
                      className="bg-white border-2 border-gray-200 rounded-3xl overflow-hidden hover:-translate-y-1 hover:border-[#E8521A]/30 transition-all duration-300 h-full"
                    >
                      <div className="p-6 flex flex-col gap-4 h-full">
                        <div className="flex items-start justify-between gap-4">
                          <div className="text-center">
                            <div className="text-4xl font-extrabold leading-none text-gray-900">{date.day}</div>
                            <div className="text-sm font-semibold text-gray-700 mt-1">{date.month}</div>
                          </div>
                          <span
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                            style={{ backgroundColor: cfg.bg, color: cfg.color }}
                          >
                            <i className={cfg.icon}></i>
                            {cfg.label}
                          </span>
                        </div>

                        <h3 className="font-bold text-[#1A1A1A] text-base leading-snug">{event.titre}</h3>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-sm text-gray-900">
                            <i className="ri-calendar-line text-black"></i>
                            <span>{formatEventScheduleLine(event)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-900">
                            <i className="ri-map-pin-line text-black"></i>
                            <span>{event.lieu}</span>
                          </div>
                        </div>

                        {showParticipantMeter ? (
                          <>
                            <div className="mt-auto">
                              <div className="flex justify-between text-sm text-gray-600 mb-1.5">
                                <span>{event.participants} participants</span>
                                <span>{event.maxParticipants} max</span>
                              </div>
                              <div className="h-2 bg-gray-300 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{
                                    width: `${Math.max(0, Math.min(pct, 100))}%`,
                                    backgroundColor: pct >= 100 ? "#dc2626" : "#16a34a"
                                  }}
                                ></div>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <i className="ri-group-line text-black"></i>
                              <span>{event.participants}/{event.maxParticipants} participants</span>
                            </div>
                          </>
                        ) : (
                          <div className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-teal-50 px-3 py-1.5 text-sm font-medium text-[#1A8C7A]">
                            <i className="ri-run-line"></i>
                            Séance d&apos;entraînement
                          </div>
                        )}

                        {actionState ? (
                          <div className="mt-4 flex justify-end">
                            {actionState === "register" ? (
                              <button
                                type="button"
                                onClick={() => void handleRegisterForEvent(event)}
                                disabled={isRegistering}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A8C7A] transition-colors hover:text-[#14685b] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <i className={`${isRegistering ? "ri-loader-4-line animate-spin" : "ri-user-add-line"}`}></i>
                                {isRegistering ? "Inscription..." : "Participer"}
                                <i className="ri-arrow-right-line"></i>
                              </button>
                            ) : actionState === "login" ? (
                              <button
                                type="button"
                                onClick={() => navigate("/formulaires?tab=login&redirect=%2Fsport%2Fevenements")}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-[#E8521A] transition-colors hover:text-[#d44a16]"
                              >
                                Se connecter pour participer
                                <i className="ri-arrow-right-line"></i>
                              </button>
                            ) : (
                              <span className={`inline-flex items-center gap-2 text-sm font-semibold ${actionState === "full" ? "text-amber-600" : "text-gray-400"}`}>
                                <i className={actionState === "full" ? "ri-alert-line" : "ri-time-line"}></i>
                                {actionState === "full" ? "Complet" : "Inscriptions clôturées"}
                              </span>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-3xl border-2 border-dashed border-gray-200 bg-white px-6 py-14 text-center">
                <i className="ri-calendar-event-line text-4xl text-gray-300"></i>
                <h3 className="mt-4 text-lg font-semibold text-[#1A1A1A]">Aucun élément à afficher</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Dès qu&apos;une rencontre ou un événement sportif est publié par l&apos;admin, il apparaîtra ici.
                </p>
              </div>
            )}
          </div>
        )}

        {activeTab === "resultats" && (
          <div>
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#1A1A1A]">Résultats &amp; Classements</h2>
              <p className="text-gray-500 mt-1">Résultats réels des rencontres terminées créées par l&apos;administration</p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {Array.from({ length: 2 }).map((_, index) => (
                  <div key={index} className="h-44 rounded-3xl border-2 border-gray-200 bg-white animate-pulse"></div>
                ))}
              </div>
            ) : resultCards.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                {resultCards.map((result) => (
                  <div
                    key={result.id}
                    className="p-6 rounded-3xl border-2 border-gray-200 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.05)] hover:border-[#E8521A]/30 transition-all duration-200"
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{result.competition}</span>
                      <span className="text-xs text-gray-400">
                        {new Date(`${result.date}T12:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex-1 text-center">
                        <div className="font-bold text-[#1A1A1A] text-sm mb-2">{result.equipe1}</div>
                        <div className="text-5xl font-extrabold text-[#E8521A]">{result.score1}</div>
                      </div>
                      <div className="flex flex-col items-center gap-1">
                        <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#FAF9F7]">
                          <i className={`${resolveSportIcon(result.type)} text-[#1A8C7A]`}></i>
                        </div>
                        <span className="text-xs text-gray-400 font-medium">VS</span>
                      </div>
                      <div className="flex-1 text-center">
                        <div className="font-bold text-[#1A1A1A] text-sm mb-2">{result.equipe2}</div>
                        <div className="text-5xl font-extrabold text-gray-300">{result.score2}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-3xl border-2 border-dashed border-gray-200 bg-white px-6 py-14 text-center mb-12">
                <i className="ri-bar-chart-box-line text-4xl text-gray-300"></i>
                <h3 className="mt-4 text-lg font-semibold text-[#1A1A1A]">Aucun résultat disponible</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Les résultats apparaîtront ici dès qu&apos;une rencontre créée par l&apos;admin sera terminée avec un score.
                </p>
              </div>
            )}

            <div>
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">Classement Football - Championnat Interne</h3>
              {standings.length > 0 ? (
                <div className="overflow-x-auto rounded-2xl border border-gray-100">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#FAF9F7]">
                        {["#", "Équipe", "J", "G", "N", "P", "Pts"].map((header) => (
                          <th key={header} className="px-4 py-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {standings.map((row) => (
                        <tr
                          key={row.equipe}
                          className={`border-t border-gray-50 hover:bg-orange-50/30 transition-colors ${row.pos === 1 ? "bg-orange-50/50" : ""}`}
                        >
                          <td className="px-4 py-3">
                            <span
                              className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${
                                row.pos === 1 ? "bg-[#E8521A] text-white" : "text-gray-400"
                              }`}
                            >
                              {row.pos}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-[#1A1A1A] text-sm">{row.equipe}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{row.j}</td>
                          <td className="px-4 py-3 text-sm text-green-600 font-medium">{row.g}</td>
                          <td className="px-4 py-3 text-sm text-gray-400">{row.n}</td>
                          <td className="px-4 py-3 text-sm text-red-400">{row.p}</td>
                          <td className="px-4 py-3 text-sm font-bold text-[#E8521A]">{row.pts}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-200 px-6 py-12 text-center text-sm text-gray-400">
                  Aucun classement football n&apos;est calculable pour le moment.
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "evenements" && (
          <div>
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-[#1A1A1A]">Prochains Événements</h2>
              <p className="text-gray-500 mt-1">Tous les événements sportifs et les rencontres à ne pas manquer</p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-[360px] rounded-3xl border-2 border-gray-200 bg-white animate-pulse"></div>
                ))}
              </div>
            ) : agendaItems.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {agendaItems.map((event) => {
                  const cfg = typeConfig[event.type];
                  const date = formatDate(event.date);
                  const pct = event.maxParticipants > 0 ? Math.round((event.participants / event.maxParticipants) * 100) : 0;
                  const actionState = getEventActionState(event, Boolean(memberUser));
                  const isRegistering = registeringEventId === event.sourceId;
                  const showParticipantMeter = event.source !== "training" && event.maxParticipants > 0;

                  return (
                    <div
                      key={event.id}
                      className="bg-white border-2 border-gray-200 rounded-3xl overflow-hidden hover:-translate-y-1 transition-all duration-300"
                    >
                      <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="text-center">
                            <div className="text-4xl font-extrabold leading-none text-gray-900">{date.day}</div>
                            <div className="text-sm font-semibold text-gray-700 mt-1">{date.month}</div>
                          </div>
                          <span
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium"
                            style={{ backgroundColor: cfg.bg, color: cfg.color }}
                          >
                            <i className={cfg.icon}></i>
                            {cfg.label}
                          </span>
                        </div>
                        <h3 className="font-bold text-[#1A1A1A] text-base mb-3 leading-snug">{event.titre}</h3>
                        <p className="text-sm text-gray-500 mb-4 leading-relaxed">{event.description}</p>
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center gap-2 text-sm text-gray-900">
                            <i className="ri-calendar-line text-black"></i>
                            {formatEventScheduleLine(event)}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-900">
                            <i className="ri-map-pin-line text-black"></i>
                            {event.lieu}
                          </div>
                        </div>
                        {showParticipantMeter ? (
                          <>
                            <div className="mb-4">
                              <div className="flex justify-between text-sm text-gray-600 mb-1.5">
                                <span>{event.participants} participants</span>
                                <span>{event.maxParticipants} max</span>
                              </div>
                              <div className="h-2 bg-gray-300 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-500"
                                  style={{
                                    width: `${Math.max(0, Math.min(pct, 100))}%`,
                                    backgroundColor: pct >= 100 ? "#dc2626" : "#16a34a"
                                  }}
                                ></div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <i className="ri-group-line text-black"></i>
                              <span>{event.participants}/{event.maxParticipants} participants</span>
                            </div>
                          </>
                        ) : (
                          <div className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1.5 text-sm font-medium text-[#1A8C7A]">
                            <i className="ri-run-line"></i>
                            Séance d&apos;entraînement
                          </div>
                        )}

                        {actionState ? (
                          <div className="mt-4 flex justify-end">
                            {actionState === "register" ? (
                              <button
                                type="button"
                                onClick={() => void handleRegisterForEvent(event)}
                                disabled={isRegistering}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-[#1A8C7A] transition-colors hover:text-[#14685b] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <i className={`${isRegistering ? "ri-loader-4-line animate-spin" : "ri-user-add-line"}`}></i>
                                {isRegistering ? "Inscription..." : "Participer"}
                                <i className="ri-arrow-right-line"></i>
                              </button>
                            ) : actionState === "login" ? (
                              <button
                                type="button"
                                onClick={() => navigate("/formulaires?tab=login&redirect=%2Fsport%2Fevenements")}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-[#E8521A] transition-colors hover:text-[#d44a16]"
                              >
                                Se connecter pour participer
                                <i className="ri-arrow-right-line"></i>
                              </button>
                            ) : (
                              <span className={`inline-flex items-center gap-2 text-sm font-semibold ${actionState === "full" ? "text-amber-600" : "text-gray-400"}`}>
                                <i className={actionState === "full" ? "ri-alert-line" : "ri-time-line"}></i>
                                {actionState === "full" ? "Complet" : "Inscriptions clôturées"}
                              </span>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-3xl border-2 border-dashed border-gray-200 bg-white px-6 py-14 text-center">
                <i className="ri-star-line text-4xl text-gray-300"></i>
                <h3 className="mt-4 text-lg font-semibold text-[#1A1A1A]">Aucun événement sportif publié</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Dès qu&apos;une rencontre ou un événement sportif est publié par l&apos;admin, il apparaîtra ici.
                </p>
              </div>
            )}

          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
